import { Directive, OnDestroy } from '@angular/core';
import { ReplaySubject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { isMapsApiLoaded } from '../utils/map-utils';
import { logger } from '../../services/logger.service';

@Directive()
export abstract class BaseGoogleApiLoader implements OnDestroy {
    api: ReplaySubject<any> = new ReplaySubject(1);

    private baseUrl = 'https://maps.google.com/maps/api/js';
    private libraries = 'places';

    protected apiUrl = `${this.baseUrl}?key=${environment.googleMapsAPIKey}&libraries=${this.libraries}&language=de&region=DE&callback=initThumediMap`;
    protected mapSelector = 'thumedi-map';

    private missingKeyWarned = false;

    ngOnDestroy(): void {
        this.api.complete();
    }

    isLoaded(): boolean {
        return isMapsApiLoaded();
    }

    /**
     * True only when a real Google Maps API key is configured. Returns false
     * when the key is empty or still holds the unresolved build-time
     * placeholder (local dev without a key, or a build where CI did not inject
     * the secret). The placeholder is recognised by its `__NAME__` shape
     * rather than by its literal value: the literal must not appear anywhere
     * in this file, otherwise it gets compiled into the bundle and trips the
     * CI dist-leak grep (Issue #7), which must only ever match a genuinely
     * un-injected key. Loaders MUST NOT append the Maps script without a key —
     * doing so would issue a request to Google with a broken key.
     */
    protected hasApiKey(): boolean {
        const key = environment.googleMapsAPIKey;
        return (
            typeof key === 'string' &&
            key.trim().length > 0 &&
            !/^__[A-Z0-9_]+__$/.test(key.trim())
        );
    }

    /**
     * Warns once per loader instance that the Maps API is disabled because no
     * key is configured, so the graceful-degradation path is observable in the
     * console without spamming it on repeated load() calls.
     */
    protected warnMissingApiKey(): void {
        if (this.missingKeyWarned) {
            return;
        }
        this.missingKeyWarned = true;
        logger.warn(
            'Google Maps API key is not configured; address autocomplete and ' +
                'distance calculation are disabled. Manual entry still works. ' +
                'See docs/secrets.md (Issue #7).'
        );
    }

    abstract load(): void;
}
